/**
 * Socket Context - React Context for Socket.IO connection
 * Provides socket instance and connection status to child components
 * Mirrors the mobile app's SocketProvider pattern
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socketService from '../services/socketService';
import { setChatConnectionStatus } from '../application/reducers/auth';
import { unlockAudio } from '../utils/notificationSound';
import toast from 'react-hot-toast';

// Create context
const SocketContext = createContext(null);

/**
 * Socket Provider Component
 * Manages socket lifecycle based on auth state
 */
export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  
  // Get auth state from Redux
  const { token, user, chatToken } = useSelector((state) => state.auth);
  
  // Local state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);
  
  // Refs for cleanup
  const connectionAttemptRef = useRef(null);
  const hasConnectedRef = useRef(false);

  /**
   * Obtain chat token from chat server
   * Uses main app credentials (email/password stored after login)
   * In production, this should be handled during main login flow
   */
  const obtainChatToken = useCallback(async () => {
    // If we already have a chat token, use it
    if (chatToken) {
      return chatToken;
    }

    // For now, we'll rely on the chat token being set during login
    // This will be connected in the auth reducer update
    console.warn('No chat token available. Chat token should be obtained during login.');
    return null;
  }, [chatToken]);

  /**
   * Initialize socket connection
   */
  const initializeSocket = useCallback(async () => {
    // Skip if no auth token or user
    if (!token || !user) {
      console.log('⏳ Waiting for authentication before connecting socket');
      return;
    }

    // Skip if already connecting
    if (isConnecting) {
      console.log('⏳ Socket connection already in progress');
      return;
    }

    // Skip if already connected
    if (socketService.getConnectionStatus()) {
      console.log('✅ Socket already connected');
      setIsConnected(true);
      return;
    }

    try {
      setIsConnecting(true);
      setConnectionError(null);

      // Get or obtain chat token
      let tokenToUse = chatToken;
      
      if (!tokenToUse) {
        console.log('🔑 No chat token, attempting to obtain...');
        tokenToUse = await obtainChatToken();
      }

      if (!tokenToUse) {
        throw new Error('Unable to obtain chat token');
      }

      // Connect socket
      console.log('🔌 Initializing socket connection...');
      await socketService.connect(tokenToUse, user);
      
      setIsConnected(true);
      hasConnectedRef.current = true;
      dispatch(setChatConnectionStatus(true));
      
      console.log('✅ Socket connected successfully');
      
    } catch (error) {
      console.error('🚫 Socket connection failed:', error);
      setConnectionError(error.message);
      setIsConnected(false);
      dispatch(setChatConnectionStatus(false));
      
      // Show error toast (only once)
      if (!hasConnectedRef.current) {
        toast.error('Unable to connect to chat server. Messages may be delayed.', {
          position: 'top-right',
          duration: 5000
        });
      }
    } finally {
      setIsConnecting(false);
    }
  }, [token, user, chatToken, isConnecting, obtainChatToken, dispatch]);

  /**
   * Disconnect socket
   */
  const disconnectSocket = useCallback(() => {
    socketService.disconnect();
    setIsConnected(false);
    dispatch(setChatConnectionStatus(false));
    hasConnectedRef.current = false;
  }, [dispatch]);

  /**
   * Reconnect socket manually
   */
  const reconnect = useCallback(async () => {
    console.log('🔄 Manual reconnection requested');
    disconnectSocket();
    
    // Small delay before reconnecting
    await new Promise(resolve => setTimeout(resolve, 1000));
    await initializeSocket();
  }, [disconnectSocket, initializeSocket]);

  // Connect socket when auth state changes
  useEffect(() => {
    if (token && user && chatToken) {
      // Clear any pending connection attempt
      if (connectionAttemptRef.current) {
        clearTimeout(connectionAttemptRef.current);
      }

      // Delay connection slightly to ensure state is settled
      connectionAttemptRef.current = setTimeout(() => {
        initializeSocket();
      }, 500);
    } else if (!token || !user) {
      // Disconnect if logged out
      disconnectSocket();
    }

    return () => {
      if (connectionAttemptRef.current) {
        clearTimeout(connectionAttemptRef.current);
      }
    };
  }, [token, user, chatToken, initializeSocket, disconnectSocket]);

  // Set up connection status listener
  useEffect(() => {
    const handleConnectionStatus = (status) => {
      setIsConnected(status.connected);
      dispatch(setChatConnectionStatus(status.connected));
      
      if (status.reconnected) {
        toast.success('Chat connection restored', {
          position: 'top-right',
          duration: 3000
        });
      }
    };

    const handleAuthenticationError = (error) => {
      console.error('🔒 Authentication error detected:', error);
      setAuthenticationFailed(true);
      setIsConnected(false);
      dispatch(setChatConnectionStatus(false));
    };

    socketService.on('connectionStatusChange', handleConnectionStatus);
    socketService.on('authenticationError', handleAuthenticationError);

    return () => {
      socketService.off('connectionStatusChange', handleConnectionStatus);
      socketService.off('authenticationError', handleAuthenticationError);
    };
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't disconnect on unmount - socket should persist across page navigation
      // disconnectSocket();
    };
  }, []);

  // Unlock Web Audio API on first user interaction (browser autoplay policy)
  useEffect(() => {
    const handleUserGesture = () => {
      unlockAudio();
      // Remove listeners after first interaction — only needs to run once
      window.removeEventListener('click', handleUserGesture);
      window.removeEventListener('keydown', handleUserGesture);
    };
    window.addEventListener('click', handleUserGesture);
    window.addEventListener('keydown', handleUserGesture);
    return () => {
      window.removeEventListener('click', handleUserGesture);
      window.removeEventListener('keydown', handleUserGesture);
    };
  }, []);

  // Context value
  const contextValue = {
    // Socket instance access
    socket: socketService.getSocket(),
    socketService,
    
    // Connection state
    isConnected,
    isConnecting,
    connectionError,
    authenticationFailed,
    
    // Actions
    connect: initializeSocket,
    disconnect: disconnectSocket,
    reconnect,
    
    // Event helpers
    emit: socketService.emit.bind(socketService),
    on: socketService.on.bind(socketService),
    off: socketService.off.bind(socketService)
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * useSocket Hook
 * Access socket context in components
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  
  return context;
};

/**
 * useSocketEvent Hook
 * Subscribe to socket events with automatic cleanup
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 */
export const useSocketEvent = (event, handler) => {
  const { socketService, isConnected } = useSocket();
  
  useEffect(() => {
    if (!isConnected || !handler) return;
    
    socketService.on(event, handler);
    
    return () => {
      socketService.off(event, handler);
    };
  }, [event, handler, isConnected, socketService]);
};

/**
 * useOnlineStatus Hook
 * Track online status of specific users
 * @param {number[]} userIds - Array of user IDs to track
 * @returns {Object} Map of userId -> online status
 */
export const useOnlineStatus = (userIds) => {
  const { socketService, isConnected } = useSocket();
  const [onlineStatus, setOnlineStatus] = useState({});

  useEffect(() => {
    if (!isConnected || !userIds?.length) return;

    // Request initial status
    socketService.getOnlineStatus(userIds, (response) => {
      if (response?.success && response?.statusList) {
        const statusMap = {};
        response.statusList.forEach((item) => {
          statusMap[item.userId] = {
            isOnline: item.status === 'online',
            lastSeen: item.lastSeen
          };
        });
        setOnlineStatus(statusMap);
      }
    });

    // Listen for status changes
    const handleStatusChange = (data) => {
      if (userIds.includes(data.userId)) {
        setOnlineStatus((prev) => ({
          ...prev,
          [data.userId]: {
            isOnline: data.status === 'online',
            lastSeen: data.lastSeen
          }
        }));
      }
    };

    socketService.onUserStatusChange(handleStatusChange);

    return () => {
      socketService.off('user:status-change', handleStatusChange);
    };
  }, [isConnected, userIds, socketService]);

  return onlineStatus;
};

export default SocketContext;
